import { audioKeyFor, getCachedAudio } from '../audioCache';
import { decode, playBuffer } from '../playBuffer';
import { ProviderUnavailable, type SpeakOpts, type SpeakResult, type TtsContext, type TtsProvider } from '../TtsProvider';

/**
 * First in the chain: exact-text audio already on the device.
 *
 * This is the provider that makes the device usable offline and makes frequent utterances
 * instant. Everything Azure synthesises for a phrase is cached here, so the phrases the person
 * uses most become the fastest — which is the right direction for a conversation aid.
 *
 * Needs no network and no gesture beyond an unlocked context, so it is available whenever there
 * is a hit.
 */
export function createCachedAudioProvider(getVoiceId: () => string): TtsProvider {
  return {
    id: 'cachedAudio',

    isAvailable(ctx: TtsContext): boolean {
      // A locked context would fail at playback, and falling through to a provider that also
      // needs Web Audio would not help — but the chain still needs an honest answer here.
      return ctx.audioUnlocked;
    },

    async speak(text: string, opts: SpeakOpts): Promise<SpeakResult> {
      const startedAtMs = performance.now();
      const voiceId = opts.voiceId ?? getVoiceId();
      const key = await audioKeyFor(text, voiceId);

      const bytes = await getCachedAudio(key);
      if (!bytes) throw new ProviderUnavailable('cachedAudio', 'no cached audio for this text');

      const buffer = await decode(bytes);
      const playback = await playBuffer(buffer, opts.signal);

      return {
        providerId: 'cachedAudio',
        startedAtMs,
        cached: true,
        ...(playback.silent ? { silent: true } : {}),
        ...(playback.startedPlayingAtMs === undefined
          ? {}
          : { audioAtMs: playback.startedPlayingAtMs }),
      };
    },
  };
}
